import type { Json, VersionedJsonDocument } from "./database.types";

export type ValidationResult<T> =
  | { ok: true; value: T }
  | { ok: false; error: { code: "invalid_payload" | "unsupported_schema_version"; message: string } };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isJson = (value: unknown): value is Json => {
  if (value === null || ["string", "number", "boolean"].includes(typeof value)) return true;
  if (Array.isArray(value)) return value.every(isJson);
  return isRecord(value) && Object.values(value).every((item) => item === undefined || isJson(item));
};

export function validateVersionedJson(
  value: unknown,
  supportedVersions: readonly number[],
  label: string,
): ValidationResult<VersionedJsonDocument> {
  if (!isRecord(value) || !Number.isInteger(value.schemaVersion) || !("data" in value) || !isJson(value.data)) {
    return {
      ok: false,
      error: {
        code: "invalid_payload",
        message: `${label} must contain a numeric schemaVersion and JSON data`,
      },
    };
  }

  if (!supportedVersions.includes(value.schemaVersion as number)) {
    return {
      ok: false,
      error: {
        code: "unsupported_schema_version",
        message: `${label} schema version ${String(value.schemaVersion)} is not supported`,
      },
    };
  }

  return { ok: true, value: value as VersionedJsonDocument };
}

const validateStoredVersionedJson = (
  value: unknown,
  storedSchemaVersion: number,
  supportedVersions: readonly number[],
  label: string,
): ValidationResult<VersionedJsonDocument> => {
  const result = validateVersionedJson(value, supportedVersions, label);
  if (!result.ok) return result;
  if (result.value.schemaVersion !== storedSchemaVersion) {
    return {
      ok: false,
      error: {
        code: "invalid_payload",
        message: `${label} schema version does not match the stored schema version`,
      },
    };
  }
  return result;
};

export const validateSectionWorkflowPayload = (value: unknown, schemaVersion: number) =>
  validateStoredVersionedJson(value, schemaVersion, [1], "Section workflow payload");

export const validateEvidencePayload = (value: unknown, schemaVersion: number) =>
  validateStoredVersionedJson(value, schemaVersion, [1], "Evidence payload");

export const validateClientDraftPayload = (value: unknown, schemaVersion: number) =>
  validateStoredVersionedJson(value, schemaVersion, [1], "Client draft payload");

export const validatePresentationDocument = (value: unknown, schemaVersion: number) => {
  const result = validateVersionedJson(value, [schemaVersion], "Presentation document");
  if (!result.ok) return result;
  if (!isRecord(result.value.data) || !Array.isArray(result.value.data.nodes)) {
    return {
      ok: false as const,
      error: {
        code: "invalid_payload" as const,
        message: "Presentation document data must contain a nodes array",
      },
    };
  }
  return result;
};

export const validatePresentationMappingSpans = (value: unknown, schemaVersion: number) => {
  const result = validateVersionedJson(value, [schemaVersion], "Presentation mapping spans");
  if (!result.ok) return result;
  if (!isRecord(result.value.data) || !Array.isArray(result.value.data.spans)) {
    return {
      ok: false as const,
      error: {
        code: "invalid_payload" as const,
        message: "Presentation mapping data must contain a spans array",
      },
    };
  }
  return result;
};

export const validateSectionTenAssessment = (value: unknown, schemaVersion: number) =>
  validateStoredVersionedJson(value, schemaVersion, [1], "Section 10 assessment");

export const validateSectionTenDraft = (value: unknown, schemaVersion: number) =>
  validateStoredVersionedJson(value, schemaVersion, [1], "Section 10 client draft");
